import React, {
  useEffect, useState, useRef, useMemo, useCallback, Fragment, ReactElement, RefObject,
} from 'react';
import Card from '../../../components/card/card.component';
import Table from '../../../components/table/table.component';
import TableHead from '../../../components/table-head/table-head.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import FilteredCell from '../../../components/filtered-cell/filtered-cell.component';
import VisibleCell from '../../../components/visible-cell/visible-cell.component';
import RequiredMark from '../../../components/required-mark/required-mark.component';
import ExpansionGroup from '../../../components/expansion-group/expansion-group.component';
import Tooltip from '../../../components/tooltip/tooltip.component';
import ContentListContainer from '../../../components/content-list-container/content-list-container.component';
import TextBlock from '../../../components/text-block/text-block.component';
import ActionButton from '../../../components/action-button/action-button.component';
import List from '../../../components/list/list.component';
import ListItem from '../../../components/list-item/list-item.component';
import ListItemText from '../../../components/list-item-text/list-item-text.component';
import ListItemSummary from '../../../components/list-item-summary/list-item-summary.component';
import TableCellShort from '../../../components/table-cell-short/table-cell-short.component';
import TableRowAddNewItem from '../../../components/table-row-add-new-item/table-row-add-new-item.component';
import TableRowSummary from '../../../components/table-row-summary/table-row-summary.component';
import TableRowSubgroupHeader from '../../../components/table-row-subgroup-header/table-row-subgroup-header.component';
import ListGroupHeader from '../../../components/list-group-header/list-group-header.component';
import ActionButtonNewItem from '../../../components/action-button-new-item/action-button-new-item.component';
import DealCapitalizationRowForm from './deal-capitalization-row-form/deal-capitalization-row-form.component';
import DealCapitalizationRow from './deal-capitalization-row/deal-capitalization-row.component';
import EditIcon from '../../../icons/edit-icon';
import RemoveIcon from '../../../icons/remove-icon';
import ArrowLinkIcon from '../../../icons/arrow-link-icon';
import StructureService from '../../../services/structure-service';
import FormatService from '../../../services/format-service';
import ScreenService from '../../../services/screen-service';
import useArrowNavigation from '../../../hooks/use-arrow-navigation';
import useScreenSmall from '../../../hooks/use-screen-small';
import useScreenMedium from '../../../hooks/use-screen-medium';
import { DEAL_COMMON_EDIT } from '../../../constants/entity/permissions';
import { TABLE_NAME_DEAL_CAPITALIZATION } from '../../../constants/system/table-names';
import { SETTING_NAME_TABLE_COLUMN_SETTINGS } from '../../../constants/entity/setting-names';

const DealCapitalization: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isSaving,
    isSaveDisabled,
    isEditMode,
    isNewDeal,
    isCustomizeMode,
    isSimpleView,
    dealCapitalizationData,
    dealCapitalizationSummaryData,
    dealCapitalizationCategory,
    dealCapitalizationNewItems,
    dealCapitalizationItemActiveIds,
    dealCapitalizationItemInlineSaveId,
    dealCapitalizationGroupInlineSaveId,
    rowIndexFrom,
    groupTitle,
    commonTitle,
    currentUser,
    createItemPermission,
    editItemPermission,
    deleteItemPermission,
    tableColumnSettings,
    dealCapitalizationTypeList,
    dealCapitalizationGroupList,
    onInlineSave,
    onInlineCancel,
    onOpenModal,
    onAddNewItem,
    onDeleteClick,
    onTableColumnsChange,
    onInlineEditModeButtonClick,
  } = props;

  const inputRefs: RefObject<Record<string, HTMLElement>> = useRef({});
  const isSmallScreen: boolean = useScreenSmall();
  const isMediumScreen: boolean = useScreenMedium();

  const [dealCapitalizationGroupItemList, setDealCapitalizationGroupItemList]
    : [DealCapitalizationGroup[] | null, Function] = useState<StateValueDefault>(null);
  const [dealCapitalizationItems, setDealCapitalizationItems]
    : [Record<string, Record<string, DealCapitalizationStatItem>> | null, Function] = useState<StateValueDefault>(null);
  const [dealCapitalizationGroupTotalItems, setDealCapitalizationGroupTotalItems]
    : [Record<string, DealCapitalizationStatItem> | null, Function] = useState<StateValueDefault>(null);
  const [dealCapitalizationTotalItems, setDealCapitalizationTotalItems]
    : [DealCapitalizationTotalItem[] | null, Function] = useState<StateValueDefault>(null);
  const [extendedTableFields, setExtendedTableFields]: [TableField[] | null, Function]
    = useState<StateValueDefault>(null);

  const dealCommonEditPermissions: string[] = useMemo((): string[] => [DEAL_COMMON_EDIT], []);
  const editItemPermissions: string[] = useMemo((): string[] => [editItemPermission], [editItemPermission]);
  const createItemPermissions: string[] = useMemo((): string[] => [createItemPermission], [createItemPermission]);
  const deleteItemPermissions: string[] = useMemo((): string[] => [deleteItemPermission], [deleteItemPermission]);

  const isCapitalizationRequired: boolean = useMemo((): boolean => (
    !!dealCapitalizationItems && Object.values(dealCapitalizationItems)
      .reduce((result: boolean, dealCapitalizationGroupItems: Record<string, DealCapitalizationStatItem>): boolean => (
        result || !!Object.keys(dealCapitalizationGroupItems).length
      ), false)
  ), [dealCapitalizationItems]);
  const dealCapitalizationItemsTotal: number = useMemo((): number => (
    dealCapitalizationGroupItemList && dealCapitalizationGroupItemList.length ? dealCapitalizationGroupItemList.reduce((
      itemsTotal: number, dealCapitalizationGroup: DealCapitalizationGroup,
    ): number => (
      itemsTotal + ((dealCapitalizationGroup.items && dealCapitalizationGroup.items.length) || 0)
    ), 0) : 0
  ), [dealCapitalizationGroupItemList]);

  const totalLabel: string = `Total ${groupTitle}s`;

  let commonRowIndex: number = 0;

  const checkIsDealCapitalizationItemActive: Function = useCallback((
    dealCapitalizationGroupId: number, dealCapitalizationItemId: number,
  ): boolean => (
    dealCapitalizationItemActiveIds && dealCapitalizationItemActiveIds[dealCapitalizationGroupId]
      ? dealCapitalizationItemActiveIds[dealCapitalizationGroupId].includes(dealCapitalizationItemId) : false
  ), [dealCapitalizationItemActiveIds]);

  const onItemChange: Function = useCallback((
    dealCapitalizationItemId: number, dealCapitalizationGroupId: number, fieldName: string, value: number,
    isNumber?: boolean, isFloat?: boolean,
  ): void => {
    setDealCapitalizationItems({
      ...(dealCapitalizationItems || {}),
      [dealCapitalizationGroupId]: {
        ...((dealCapitalizationItems && dealCapitalizationItems[dealCapitalizationGroupId]) || {}),
        [dealCapitalizationItemId]: {
          ...(
            (dealCapitalizationItems && dealCapitalizationItems[dealCapitalizationGroupId]
              && dealCapitalizationItems[dealCapitalizationGroupId][dealCapitalizationItemId]) || {}
          ),
          [fieldName]: isNumber ? FormatService.formatCleanNumber(value.toString(), isFloat) : value,
        },
      },
    });
  }, [dealCapitalizationItems, dealCapitalizationNewItems]);

  const openModal: Function = useCallback((
    dealCapitalizationItem: DealCapitalizationItem, dealCapitalizationGroup: DealCapitalizationGroup,
  ): void => {
    if (onOpenModal) {
      onOpenModal(dealCapitalizationItem, dealCapitalizationGroup);
    }
  }, []);

  const onRowClick: Function = useCallback((
    dealCapitalizationItem: DealCapitalizationItem,
    dealCapitalizationGroup: DealCapitalizationGroup,
    isListView?: boolean,
  ): void => {
    const withNoSelection: boolean = ScreenService.checkNoSelection();

    if (withNoSelection && !isCustomizeMode && ((!isEditMode && !isNewDeal) || isListView)) {
      openModal(dealCapitalizationItem, dealCapitalizationGroup);
    }
  }, [isCustomizeMode, isEditMode, isNewDeal]);

  const onActionCellClick: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    event.stopPropagation();
  }, []);

  const addNewItem: Function = useCallback((dealCapitalizationGroupId: number): void => {
    if (onAddNewItem) {
      onAddNewItem(dealCapitalizationGroupId, {
        id: StructureService.getNewItemId(
          (dealCapitalizationNewItems && dealCapitalizationNewItems[dealCapitalizationGroupId]) || [],
          dealCapitalizationGroupItemList || undefined,
          'items',
        ),
        type: null,
        name: null,
      });
    }
  }, [dealCapitalizationGroupItemList, dealCapitalizationNewItems, dealCapitalizationItems]);

  const deleteClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealCapitalizationItemId: number,
    dealCapitalizationGroupId: number, isNewItem?: boolean,
  ): void => {
    if (onDeleteClick) {
      onDeleteClick(dealCapitalizationItemId, dealCapitalizationGroupId, isNewItem);
    }

    if (dealCapitalizationItems) {
      const newDealCapitalizationItems: Record<string, Record<string, DealCapitalizationStatItem>> = {
        ...dealCapitalizationItems,
      };
      delete newDealCapitalizationItems[dealCapitalizationGroupId][dealCapitalizationItemId];
      setDealCapitalizationItems(newDealCapitalizationItems);
    }

    if (!isNewItem && dealCapitalizationGroupItemList && dealCapitalizationGroupItemList.length) {
      const newDealCapitalizationGroupItemList: DealCapitalizationGroup[] = [
        ...dealCapitalizationGroupItemList.map((
          dealCapitalizationGroupItem: DealCapitalizationGroup,
        ): DealCapitalizationGroup => ({
          ...dealCapitalizationGroupItem,
          items: [...dealCapitalizationGroupItem.items],
        })),
      ];

      newDealCapitalizationGroupItemList.forEach((
        dealCapitalizationGroupItem: DealCapitalizationGroup, groupIndex: number,
      ): void => {
        if (dealCapitalizationGroupItem.items && dealCapitalizationGroupItem.items.length
          && dealCapitalizationGroupItem.id === dealCapitalizationGroupId) {
          dealCapitalizationGroupItem.items.forEach((
            dealCapitalizationItem: DealCapitalizationItem, dealCapitalizationItemIndex: number,
          ): void => {
            if (dealCapitalizationItem.id === dealCapitalizationItemId) {
              StructureService.deleteListItemByIndex(
                newDealCapitalizationGroupItemList[groupIndex].items,
                dealCapitalizationItemIndex,
              );
            }
          });
        }
      });
      setDealCapitalizationGroupItemList(newDealCapitalizationGroupItemList);
    }

    event.stopPropagation();
  }, [dealCapitalizationGroupItemList, dealCapitalizationItems, dealCapitalizationNewItems]);

  const tableDealCapitalizationItemListFields: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'name',
      title: isSimpleView ? `${groupTitle} Name` : 'Name',
      isRequired: isEditMode || isNewDeal,
      isSticky: !isEditMode && !isNewDeal,
      component: (dealCapitalizationItem: DealCapitalizationItem): ReactElement => (
        <TableCellShort isText isSticky={!isEditMode && !isNewDeal} isStickyLeft={!isEditMode && !isNewDeal}>
          {dealCapitalizationItem.name}
        </TableCellShort>
      ),
      summaryComponent: (dealCapitalizationItem: DealCapitalizationItem, totalName: string): ReactElement => (
        <TableCellShort isText isSticky={!isEditMode && !isNewDeal} isStickyLeft={!isEditMode && !isNewDeal}>
          {totalName}
        </TableCellShort>
      ),
    },
    {
      id: 2,
      name: 'type.title',
      title: 'Type',
      isRequired: isEditMode || isNewDeal,
      component: (dealCapitalizationItem: DealCapitalizationItem): ReactElement => (
        <TableCell>
          {dealCapitalizationItem.type && dealCapitalizationItem.type.title}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 3,
      name: 'atClosingAmount',
      title: 'At Closing, $',
      isNumber: true,
      isFullTableOnly: true,
      component: (dealCapitalizationItem: DealCapitalizationItem): ReactElement => (
        <TableCell isNumber isTextRight={!dealCapitalizationItem.atClosingAmount}>
          {dealCapitalizationItem.atClosingAmount || '-'}
        </TableCell>
      ),
      summaryComponent: (
        dealCapitalizationItem: DealCapitalizationItem, totalName: string, isEditableState?: boolean,
      ): ReactElement => (
        <TableCell isNumber={!isEditableState}>
          {(dealCapitalizationItem && dealCapitalizationItem.atClosingAmount && FormatService.formatNumber(
            dealCapitalizationItem.atClosingAmount, true,
          )) || ''}
        </TableCell>
      ),
    },
    {
      id: 4,
      name: 'futureAmount',
      title: 'Future, $',
      isNumber: true,
      isFullTableOnly: true,
      component: (dealCapitalizationItem: DealCapitalizationItem): ReactElement => (
        <TableCell isNumber isTextRight={!dealCapitalizationItem.futureAmount}>
          {dealCapitalizationItem.futureAmount || '-'}
        </TableCell>
      ),
      summaryComponent: (
        dealCapitalizationItem: DealCapitalizationItem, totalName: string, isEditableState?: boolean,
      ): ReactElement => (
        <TableCell isNumber={!isEditableState}>
          {(dealCapitalizationItem && dealCapitalizationItem.futureAmount && FormatService.formatNumber(
            dealCapitalizationItem.futureAmount, true,
          )) || ''}
        </TableCell>
      ),
    },
    {
      id: 5,
      name: 'amount',
      title: 'Total Amount, $',
      isNumber: true,
      component: (dealCapitalizationItem: DealCapitalizationItem): ReactElement => (
        <TableCell isNumber isTextRight={!dealCapitalizationItem.amount}>
          {dealCapitalizationItem.amount || '-'}
        </TableCell>
      ),
      summaryComponent: (
        dealCapitalizationItem: DealCapitalizationItem, totalName: string, isEditableState?: boolean,
      ): ReactElement => (
        <TableCell isNumber={!isEditableState}>
          {(dealCapitalizationItem && dealCapitalizationItem.amount && FormatService.formatNumber(
            dealCapitalizationItem.amount, true,
          )) || ''}
        </TableCell>
      ),
    },
    {
      id: 6,
      name: 'gsfAmount',
      title: '$ / GSF',
      isNumber: true,
      isFullTableOnly: true,
      component: (dealCapitalizationItem: DealCapitalizationItem): ReactElement => (
        <TableCell isNumber isTextRight={!dealCapitalizationItem.gsfAmount}>
          {dealCapitalizationItem.gsfAmount || '-'}
        </TableCell>
      ),
      summaryComponent: (
        dealCapitalizationItem: DealCapitalizationItem, totalName: string, isEditableState?: boolean,
      ): ReactElement => (
        <TableCell isNumber={!isEditableState}>
          {(dealCapitalizationItem && dealCapitalizationItem.gsfAmount && FormatService.formatNumber(
            dealCapitalizationItem.gsfAmount, true,
          )) || ''}
        </TableCell>
      ),
    },
    {
      id: 7,
      name: 'nsfAmount',
      title: '$ / NSF',
      isNumber: true,
      isFullTableOnly: true,
      component: (dealCapitalizationItem: DealCapitalizationItem): ReactElement => (
        <TableCell isNumber isTextRight={!dealCapitalizationItem.nsfAmount}>
          {dealCapitalizationItem.nsfAmount || '-'}
        </TableCell>
      ),
      summaryComponent: (
        dealCapitalizationItem: DealCapitalizationItem, totalName: string, isEditableState?: boolean,
      ): ReactElement => (
        <TableCell isNumber={!isEditableState}>
          {(dealCapitalizationItem && dealCapitalizationItem.nsfAmount && FormatService.formatNumber(
            dealCapitalizationItem.nsfAmount, true,
          )) || ''}
        </TableCell>
      ),
    },
    {
      id: 8,
      name: 'zfaAmount',
      title: '$ / ZFA',
      isNumber: true,
      isFullTableOnly: true,
      component: (dealCapitalizationItem: DealCapitalizationItem): ReactElement => (
        <TableCell isNumber isTextRight={!dealCapitalizationItem.zfaAmount}>
          {dealCapitalizationItem.zfaAmount || '-'}
        </TableCell>
      ),
      summaryComponent: (
        dealCapitalizationItem: DealCapitalizationItem, totalName: string, isEditableState?: boolean,
      ): ReactElement => (
        <TableCell isNumber={!isEditableState}>
          {(dealCapitalizationItem && dealCapitalizationItem.zfaAmount && FormatService.formatNumber(
            dealCapitalizationItem.zfaAmount, true,
          )) || ''}
        </TableCell>
      ),
    },
    {
      id: 9,
      name: 'unitsAmount',
      title: '$ / Unit',
      isNumber: true,
      isFullTableOnly: true,
      component: (dealCapitalizationItem: DealCapitalizationItem): ReactElement => (
        <TableCell isNumber isTextRight={!dealCapitalizationItem.unitsAmount}>
          {dealCapitalizationItem.unitsAmount || '-'}
        </TableCell>
      ),
      summaryComponent: (
        dealCapitalizationItem: DealCapitalizationItem, totalName: string, isEditableState?: boolean,
      ): ReactElement => (
        <TableCell isNumber={!isEditableState}>
          {(dealCapitalizationItem && dealCapitalizationItem.unitsAmount && FormatService.formatNumber(
            dealCapitalizationItem.unitsAmount, true,
          )) || ''}
        </TableCell>
      ),
    },
    {
      id: 10,
      name: 'keysAmount',
      title: '$ / Key',
      isNumber: true,
      isFullTableOnly: true,
      component: (dealCapitalizationItem: DealCapitalizationItem): ReactElement => (
        <TableCell isNumber isTextRight={!dealCapitalizationItem.keysAmount}>
          {dealCapitalizationItem.keysAmount || '-'}
        </TableCell>
      ),
      summaryComponent: (
        dealCapitalizationItem: DealCapitalizationItem, totalName: string, isEditableState?: boolean,
      ): ReactElement => (
        <TableCell isNumber={!isEditableState}>
          {(dealCapitalizationItem && dealCapitalizationItem.keysAmount && FormatService.formatNumber(
            dealCapitalizationItem.keysAmount, true,
          )) || ''}
        </TableCell>
      ),
    },
    {
      id: 11,
      name: 'acresAmount',
      title: '$ / Acre',
      isNumber: true,
      isFullTableOnly: true,
      component: (dealCapitalizationItem: DealCapitalizationItem): ReactElement => (
        <TableCell isNumber isTextRight={!dealCapitalizationItem.acresAmount}>
          {dealCapitalizationItem.acresAmount || '-'}
        </TableCell>
      ),
      summaryComponent: (
        dealCapitalizationItem: DealCapitalizationItem, totalName: string, isEditableState?: boolean,
      ): ReactElement => (
        <TableCell isNumber={!isEditableState}>
          {(dealCapitalizationItem && dealCapitalizationItem.acresAmount && FormatService.formatNumber(
            dealCapitalizationItem.acresAmount, true,
          )) || ''}
        </TableCell>
      ),
    },
    {
      id: 12,
      name: 'lotsfAmount',
      title: '$ / Lot SF',
      isNumber: true,
      isFullTableOnly: true,
      component: (dealCapitalizationItem: DealCapitalizationItem): ReactElement => (
        <TableCell isNumber isTextRight={!dealCapitalizationItem.lotsfAmount}>
          {dealCapitalizationItem.lotsfAmount || '-'}
        </TableCell>
      ),
      summaryComponent: (
        dealCapitalizationItem: DealCapitalizationItem, totalName: string, isEditableState?: boolean,
      ): ReactElement => (
        <TableCell isNumber={!isEditableState}>
          {(dealCapitalizationItem && dealCapitalizationItem.lotsfAmount && FormatService.formatNumber(
            dealCapitalizationItem.lotsfAmount, true,
          )) || ''}
        </TableCell>
      ),
    },
    {
      id: 13,
      name: 'sharePercent',
      title: '% Share',
      isNumber: true,
      component: (dealCapitalizationItem: DealCapitalizationItem): ReactElement => (
        <TableCell isNumber isTextRight={!dealCapitalizationItem.sharePercent}>
          {dealCapitalizationItem.sharePercent || '-'}
        </TableCell>
      ),
      summaryComponent: (
        dealCapitalizationItem: DealCapitalizationItem, totalName: string, isEditableState?: boolean,
      ): ReactElement => (
        <TableCell isNumber={!isEditableState}>
          {(dealCapitalizationItem && dealCapitalizationItem.sharePercent) || ''}
        </TableCell>
      ),
    },
    {
      id: 14,
      name: 'notes',
      title: 'Notes',
      isFullTableOnly: true,
      component: (dealCapitalizationItem: DealCapitalizationItem): ReactElement => (
        <TableCell isTextMultiline>
          {dealCapitalizationItem.notes || '-'}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 15,
      name: 'actions',
      title: 'Actions',
      isNumber: false,
      isHeaderNameHidden: true,
      isHidden: isSimpleView && !isEditMode && !isNewDeal,
      isSticky: true,
      isStickyRight: true,
      component: (
        dealCapitalizationItem: DealCapitalizationItem, dealCapitalizationGroupItem: DealCapitalizationGroup,
      ): ReactElement => (
        <TableCell isActions isTextRight isSticky isStickyRight onClick={onActionCellClick}>
          {
            !isSimpleView && !isCustomizeMode && (!isEditMode
              || !checkIsDealCapitalizationItemActive(dealCapitalizationGroupItem.id, dealCapitalizationItem.id)) && (
              <CheckAccess permissions={dealCommonEditPermissions}>
                <ActionButton
                  withSmallPadding
                  isHidden={!!isSaving}
                  onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                    onInlineEditModeButtonClick(event, dealCapitalizationGroupItem.id, dealCapitalizationItem.id);
                  }}
                >
                  <EditIcon />
                </ActionButton>
              </CheckAccess>
            )
          }
        </TableCell>
      ),
      summaryComponent: (): ReactElement | boolean => (
        <TableCell isActions isSticky isStickyRight />
      ),
    },
  ], [isEditMode, isNewDeal, isCustomizeMode, isSimpleView, dealCapitalizationItemActiveIds, isSaving]);

  const columnsTotal: number = tableDealCapitalizationItemListFields.length;
  const simpleViewColumnsTotal: number = tableDealCapitalizationItemListFields
    .filter((tableField: TableField): boolean => !tableField.isFullTableOnly).length;

  useEffect((): void => {
    if (dealCapitalizationItems) {
      const initialTotalItems: DealCapitalizationTotalItem = {
        atClosingAmount: 0,
        futureAmount: 0,
        amount: 0,
        sharePercent: 100,
      };
      const initialGroupTotalItems: Record<string, DealCapitalizationStatItem> = {};
      const initialItems: Record<string, Record<string, DealCapitalizationStatItem>> = dealCapitalizationItems;

      Object.values(dealCapitalizationItems).forEach((
        dealCapitalizationGroupItems: Record<string, DealCapitalizationStatItem>,
      ): void => {
        Object.values(dealCapitalizationGroupItems).forEach((
          dealCapitalizationItem: DealCapitalizationStatItem,
        ): void => {
          initialTotalItems.amount += dealCapitalizationItem.amount;
        });
      });

      Object.entries(dealCapitalizationItems)
        .forEach((
          [dealCapitalizationGroupId, dealCapitalizationGroupItems]
          : [string, Record<string, DealCapitalizationStatItem>],
        ): void => {
          if (!initialGroupTotalItems[dealCapitalizationGroupId]) {
            initialGroupTotalItems[dealCapitalizationGroupId] = {
              atClosingAmount: 0,
              futureAmount: 0,
              amount: 0,
              pricing: 0,
              sharePercent: 0,
            };
          }

          let currentPricingType: string = '';
          let isSamePricingType: boolean = true;
          Object.entries(dealCapitalizationGroupItems)
            .forEach((
              [dealCapitalizationItemId, dealCapitalizationItem]: [string, DealCapitalizationStatItem],
            ): void => {
              if (dealCapitalizationItem.pricingType) {
                if (!currentPricingType) {
                  currentPricingType = dealCapitalizationItem.pricingType;
                } else if (currentPricingType !== dealCapitalizationItem.pricingType) {
                  isSamePricingType = false;
                }
              }

              initialItems[dealCapitalizationGroupId][dealCapitalizationItemId].sharePercent = (
                dealCapitalizationItem.atClosingAmount
                  ? ((dealCapitalizationItem.atClosingAmount / initialTotalItems.atClosingAmount) * 100) : 0
              );
              initialItems[dealCapitalizationGroupId][dealCapitalizationItemId].sharePercent = (
                dealCapitalizationItem.futureAmount
                  ? ((dealCapitalizationItem.futureAmount / initialTotalItems.futureAmount) * 100) : 0
              );
              initialItems[dealCapitalizationGroupId][dealCapitalizationItemId].sharePercent = (
                dealCapitalizationItem.amount ? ((dealCapitalizationItem.amount / initialTotalItems.amount) * 100) : 0
              );
              initialGroupTotalItems[dealCapitalizationGroupId].atClosingAmount += (
                dealCapitalizationItem.atClosingAmount
              );
              initialGroupTotalItems[dealCapitalizationGroupId].futureAmount += dealCapitalizationItem.futureAmount;
              initialGroupTotalItems[dealCapitalizationGroupId].amount += dealCapitalizationItem.amount;
              initialGroupTotalItems[dealCapitalizationGroupId].pricing += (
                dealCapitalizationItem.amount * dealCapitalizationItem.pricing
              );
              initialGroupTotalItems[dealCapitalizationGroupId].sharePercent += (
                dealCapitalizationItem.sharePercent
              );
            });

          initialGroupTotalItems[dealCapitalizationGroupId].pricing = isSamePricingType
          && initialGroupTotalItems[dealCapitalizationGroupId].pricing
          && initialGroupTotalItems[dealCapitalizationGroupId].amount
            ? initialGroupTotalItems[dealCapitalizationGroupId].pricing
            / initialGroupTotalItems[dealCapitalizationGroupId].amount : 0;
          initialGroupTotalItems[dealCapitalizationGroupId].sharePercent = FormatService.parseFloat(
            FormatService.formatFixedFloatNumber(
              (initialGroupTotalItems[dealCapitalizationGroupId].amount / initialTotalItems.amount) * 100,
            ),
          );
          initialGroupTotalItems[dealCapitalizationGroupId].amount = (
            initialGroupTotalItems[dealCapitalizationGroupId].amount
          );
        });

      initialTotalItems.atClosingAmount = initialTotalItems.atClosingAmount || 0;
      initialTotalItems.futureAmount = initialTotalItems.futureAmount || 0;
      initialTotalItems.amount = initialTotalItems.amount || 0;
      initialTotalItems.sharePercent = initialTotalItems.sharePercent && initialTotalItems.amount
        ? initialTotalItems.sharePercent : 0;

      setDealCapitalizationItems(initialItems);
      setDealCapitalizationGroupTotalItems(initialGroupTotalItems);
      setDealCapitalizationTotalItems(initialTotalItems);
    }
  }, [dealCapitalizationItems]);

  useEffect((): void => {
    if (dealCapitalizationData) {
      const initialDealCapitalizationItems: Record<string, Record<string, DealCapitalizationStatItem>> = {};
      dealCapitalizationData.forEach((dealCapitalizationGroup: DealCapitalizationGroup): void => {
        if (!initialDealCapitalizationItems[dealCapitalizationGroup.id]) {
          initialDealCapitalizationItems[dealCapitalizationGroup.id] = {};
        }

        dealCapitalizationGroup.items.forEach((dealCapitalizationItem: DealCapitalizationItem): void => {
          initialDealCapitalizationItems[dealCapitalizationGroup.id][dealCapitalizationItem.id] = {
            atClosingAmount: FormatService.formatCleanNumber(dealCapitalizationItem.atClosingAmount),
            futureAmount: FormatService.formatCleanNumber(dealCapitalizationItem.futureAmount),
            amount: FormatService.formatCleanNumber(dealCapitalizationItem.amount),
            pricing: FormatService.formatCleanNumber(dealCapitalizationItem.pricing, true),
            pricingType: dealCapitalizationItem.pricingType && dealCapitalizationItem.pricingType.name,
            sharePercent: FormatService.formatCleanNumber(dealCapitalizationItem.sharePercent, true),
          };
        });
      });
      setDealCapitalizationItems(initialDealCapitalizationItems);
      setDealCapitalizationGroupItemList([...dealCapitalizationData]);
    }
  }, [dealCapitalizationData, isEditMode]);

  useEffect((): void => {
    if (currentUser && !tableColumnSettings) {
      const tableColumnSettingsCommon: Setting = currentUser.settings
        && currentUser.settings.find((settingItem: Setting): boolean => (
          settingItem.name === SETTING_NAME_TABLE_COLUMN_SETTINGS
        ));

      if (onTableColumnsChange) {
        onTableColumnsChange(
          tableColumnSettingsCommon && tableColumnSettingsCommon.value
          && tableColumnSettingsCommon.value[TABLE_NAME_DEAL_CAPITALIZATION],
          true,
        );
      }
    }
  }, [currentUser]);

  useEffect((): void => {
    setExtendedTableFields(tableDealCapitalizationItemListFields);
  }, [tableDealCapitalizationItemListFields]);

  useArrowNavigation(inputRefs);

  return (
    <CheckAccess permissions={editItemPermissions} isEnabled={isEditMode || isNewDeal}>
      {
        ((isEditMode || isNewDeal || !!dealCapitalizationItemsTotal)
          && ((isSimpleView && (!isMediumScreen || !!dealCapitalizationItemsTotal)) || !isSimpleView)) && (
          <ExpansionGroup
            isDefaultExpanded
            isExpanded={isSimpleView}
            isFixed={isSimpleView}
            withExpandedOffset={!isSimpleView}
            headerComponent={isSimpleView ? commonTitle : `${groupTitle}s`}
          >
            {
              ((isSimpleView && !isMediumScreen) || (!isSimpleView && !isSmallScreen)) && (
                <Card>
                  <ContentListContainer isDefault>
                    <Table isFullWidth={isEditMode || isNewDeal}>
                      <TableHead isSubHeader withFixedCellsSize={!isSimpleView}>
                        <TableRow>
                          {
                            extendedTableFields && !!extendedTableFields.length
                            && extendedTableFields.map((tableField: TableField): ReactElement => (
                              <VisibleCell
                                isFullTable={!isSimpleView}
                                isEditMode={isEditMode || isNewDeal}
                                key={tableField.id}
                                tableField={tableField}
                              >
                                <FilteredCell
                                  columnName={tableField.name}
                                  tableName={TABLE_NAME_DEAL_CAPITALIZATION}
                                  currentUser={currentUser}
                                >
                                  <TableCell
                                    isNumber={tableField.isNumber}
                                    isSticky={!isCustomizeMode && tableField.isSticky}
                                    isStickyLeft={!isCustomizeMode && tableField.isSticky && !tableField.isStickyRight}
                                    isStickyRight={!isCustomizeMode && tableField.isSticky && tableField.isStickyRight}
                                    isStickyRightHeader={!isCustomizeMode && tableField.isSticky
                                      && tableField.isStickyRight}
                                  >
                                    {
                                      !tableField.isHeaderNameHidden && (
                                        <TextBlock>
                                          {tableField.title}
                                          {
                                            (isNewDeal || isEditMode) && tableField.isRequired
                                            && isCapitalizationRequired && <RequiredMark />
                                          }
                                        </TextBlock>
                                      )
                                    }
                                  </TableCell>
                                </FilteredCell>
                              </VisibleCell>
                            ))
                          }
                        </TableRow>
                      </TableHead>
                      <TableBody
                        isPlain
                        withZebraRows
                        withFixedCellsSize
                        withBorderedCells
                        withFixedCellsSizeExtended={(isEditMode || isNewDeal) && !isSimpleView}
                      >
                        {
                          isSimpleView && (
                            <Fragment>
                              {
                                dealCapitalizationGroupItemList && !!dealCapitalizationGroupItemList.length && (
                                  dealCapitalizationGroupItemList.map((
                                    dealCapitalizationGroupItem: DealCapitalizationGroup,
                                  ): ReactElement => {
                                    const groupId: number = dealCapitalizationGroupItem.id;
                                    const commonDealCapitalizationItemIndex: number = commonRowIndex;
                                    commonRowIndex++;

                                    return (
                                      ((dealCapitalizationGroupItem.items
                                          && !!dealCapitalizationGroupItem.items.length)
                                        || (isEditMode || isNewDeal)) && (
                                        <Fragment key={groupId}>
                                          {
                                            dealCapitalizationGroupItem.items
                                            && !!dealCapitalizationGroupItem.items.length
                                            && dealCapitalizationGroupItem.items.map((
                                              dealCapitalizationItem: DealCapitalizationItem,
                                            ): ReactElement => {
                                              const itemId: number = dealCapitalizationItem.id;

                                              return (
                                                <DealCapitalizationRow
                                                  isActive={checkIsDealCapitalizationItemActive(groupId, itemId)}
                                                  isSaving={isSaving}
                                                  isSaveDisabled={isSaveDisabled}
                                                  isInlineSaving={(
                                                    dealCapitalizationItemInlineSaveId !== null
                                                    && dealCapitalizationGroupInlineSaveId !== null
                                                      ? itemId === dealCapitalizationItemInlineSaveId
                                                      && groupId === dealCapitalizationGroupInlineSaveId
                                                      : isSaving
                                                  )}
                                                  isEditMode={isEditMode}
                                                  isNewDeal={isNewDeal}
                                                  isCustomizeMode={isCustomizeMode}
                                                  isSimpleView={isSimpleView}
                                                  key={`${groupId}-${itemId}`}
                                                  dealCapitalizationItem={dealCapitalizationItem}
                                                  dealCapitalizationGroup={dealCapitalizationGroupItem}
                                                  dealCapitalizationItemIndex={commonDealCapitalizationItemIndex}
                                                  dealCapitalizationCategory={dealCapitalizationCategory}
                                                  dealCapitalizationDynamicData={(dealCapitalizationItems
                                                    && dealCapitalizationItems[groupId]
                                                    && dealCapitalizationItems[groupId][itemId])}
                                                  dealCapitalizationNewItems={dealCapitalizationNewItems}
                                                  dealCapitalizationItems={dealCapitalizationItems}
                                                  inputRefs={inputRefs}
                                                  deleteItemPermissions={deleteItemPermissions}
                                                  tableDealCapitalizationListFields={extendedTableFields}
                                                  currentUser={currentUser}
                                                  dealCapitalizationTypeList={dealCapitalizationTypeList}
                                                  dealCapitalizationGroupList={dealCapitalizationGroupList}
                                                  onInlineSave={onInlineSave}
                                                  onInlineCancel={onInlineCancel}
                                                  onItemChange={onItemChange}
                                                  onDeleteClick={deleteClick}
                                                  onOpenModal={openModal}
                                                />
                                              );
                                            })
                                          }
                                          {
                                            (isEditMode || isNewDeal) && dealCapitalizationNewItems[groupId]
                                            && !!dealCapitalizationNewItems[groupId].length
                                            && dealCapitalizationNewItems[groupId]
                                              .map((dealCapitalizationItem: DealCapitalizationItem): ReactElement => {
                                                const itemId: number = dealCapitalizationItem.id;

                                                return (
                                                  <DealCapitalizationRowForm
                                                    isNewEntity
                                                    isSimpleView
                                                    isActive={!isNewDeal}
                                                    isSaving={isSaving}
                                                    isSaveDisabled={isSaveDisabled}
                                                    isInlineSaving={(
                                                      dealCapitalizationItemInlineSaveId !== null
                                                      && dealCapitalizationGroupInlineSaveId !== null
                                                        ? itemId === dealCapitalizationItemInlineSaveId
                                                        && groupId === dealCapitalizationGroupInlineSaveId
                                                        : isSaving
                                                    )}
                                                    key={`${groupId}-${itemId}`}
                                                    dealCapitalizationItemIndex={(
                                                      commonDealCapitalizationItemIndex + (rowIndexFrom || 0)
                                                    )}
                                                    dealCapitalizationItemData={dealCapitalizationItem}
                                                    dealCapitalizationGroupData={dealCapitalizationGroupItem}
                                                    dealCapitalizationCategory={dealCapitalizationCategory}
                                                    dealCapitalizationItemDynamicData={(
                                                      dealCapitalizationItems && dealCapitalizationItems[groupId]
                                                      && dealCapitalizationItems[groupId][itemId]
                                                    )}
                                                    dealCapitalizationNewItems={dealCapitalizationNewItems}
                                                    dealCapitalizationItems={dealCapitalizationItems}
                                                    inputRefs={inputRefs}
                                                    currentUser={currentUser}
                                                    deleteItemPermissions={deleteItemPermissions}
                                                    dealCapitalizationTypeList={dealCapitalizationTypeList}
                                                    dealCapitalizationGroupList={dealCapitalizationGroupList}
                                                    onOpenModal={(): void => {
                                                      openModal(dealCapitalizationItem, dealCapitalizationGroupItem);
                                                    }}
                                                    onDeleteClick={(
                                                      event: React.MouseEvent<Element, MouseEvent>,
                                                    ): void => {
                                                      deleteClick(event, itemId, groupId, true);
                                                    }}
                                                    onItemChange={onItemChange}
                                                    onInlineSave={onInlineSave}
                                                    onInlineCancel={onInlineCancel}
                                                  />
                                                );
                                              })
                                          }
                                          {
                                            (isEditMode || isNewDeal) && (
                                              <CheckAccess permissions={createItemPermissions}>
                                                <TableRowAddNewItem
                                                  label={`Add New ${groupTitle}`}
                                                  colSpan={isSimpleView ? simpleViewColumnsTotal : columnsTotal}
                                                  onClick={(): void => {
                                                    addNewItem(groupId);
                                                  }}
                                                />
                                              </CheckAccess>
                                            )
                                          }
                                          {
                                            (dealCapitalizationGroupItem.name && dealCapitalizationGroupTotalItems
                                              && dealCapitalizationGroupTotalItems[groupId]) && (
                                              <TableRowSummary withNoHighlight withOffset={isEditMode || isNewDeal}>
                                                {
                                                  extendedTableFields && !!extendedTableFields.length
                                                  && extendedTableFields.map((tableField: TableField): ReactElement => (
                                                    <VisibleCell
                                                      isFullTable={!isSimpleView}
                                                      isEditMode={isEditMode || isNewDeal}
                                                      key={tableField.id}
                                                      tableField={tableField}
                                                    >
                                                      {
                                                        !!tableField.summaryComponent && (
                                                          tableField.summaryComponent(
                                                            isEditMode || isNewDeal ? (dealCapitalizationGroupTotalItems
                                                                && dealCapitalizationGroupTotalItems[groupId])
                                                              : dealCapitalizationGroupItem.summary,
                                                            `Total ${dealCapitalizationGroupItem.name}`,
                                                            isEditMode || isNewDeal,
                                                          )
                                                        )
                                                      }
                                                    </VisibleCell>
                                                  ))
                                                }
                                              </TableRowSummary>
                                            )
                                          }
                                        </Fragment>
                                      )
                                    );
                                  })
                                )
                              }
                              {
                                ((!isEditMode && !isNewDeal && dealCapitalizationSummaryData)
                                  || ((isEditMode || isNewDeal) && dealCapitalizationTotalItems)) && (
                                  <TableRowSummary withNoHighlight withOffset={isEditMode || isNewDeal}>
                                    {
                                      extendedTableFields && !!extendedTableFields.length
                                      && extendedTableFields.map((tableField: TableField): ReactElement => (
                                        <VisibleCell
                                          isFullTable={!isSimpleView}
                                          isEditMode={isEditMode || isNewDeal}
                                          key={tableField.id}
                                          tableField={tableField}
                                        >
                                          {
                                            !!tableField.summaryComponent && (
                                              <FilteredCell
                                                tableName={TABLE_NAME_DEAL_CAPITALIZATION}
                                                columnName={tableField.name}
                                                currentUser={currentUser}
                                              >
                                                {
                                                  tableField.summaryComponent(
                                                    isEditMode || isNewDeal
                                                      ? dealCapitalizationTotalItems : dealCapitalizationSummaryData,
                                                    totalLabel,
                                                    isEditMode || isNewDeal,
                                                  )
                                                }
                                              </FilteredCell>
                                            )
                                          }
                                        </VisibleCell>
                                      ))
                                    }
                                  </TableRowSummary>
                                )
                              }
                            </Fragment>
                          )
                        }
                        {
                          !isSimpleView && (
                            <Fragment>
                              {
                                dealCapitalizationGroupItemList && !!dealCapitalizationGroupItemList.length
                                && dealCapitalizationGroupItemList.map((
                                  dealCapitalizationGroupItem: DealCapitalizationGroup,
                                ): ReactElement => {
                                  const groupId: number = dealCapitalizationGroupItem.id;

                                  return (
                                    <Fragment key={groupId}>
                                      {
                                        dealCapitalizationGroupItemList.length > 1 && (
                                          <TableRowSubgroupHeader>
                                            <TableCell
                                              isSticky={!isEditMode && !isNewDeal}
                                              isStickyLeft={!isEditMode && !isNewDeal}
                                            >
                                              {dealCapitalizationGroupItem.name}
                                            </TableCell>
                                            <TableCell colSpan={columnsTotal - 1} />
                                          </TableRowSubgroupHeader>
                                        )
                                      }
                                      {
                                        dealCapitalizationGroupItem.items && !!dealCapitalizationGroupItem.items.length
                                        && dealCapitalizationGroupItem.items.map((
                                          dealCapitalizationItem: DealCapitalizationItem,
                                        ): ReactElement => {
                                          const itemId: number = dealCapitalizationItem.id;
                                          const isActive: boolean = checkIsDealCapitalizationItemActive(
                                            groupId, itemId,
                                          );
                                          const isInlineSaving: boolean = dealCapitalizationItemInlineSaveId !== null
                                          && dealCapitalizationGroupInlineSaveId !== null
                                            ? itemId === dealCapitalizationItemInlineSaveId
                                            && groupId === dealCapitalizationGroupInlineSaveId
                                            : isSaving;
                                          const commonDealCapitalizationItemIndex: number = commonRowIndex;
                                          commonRowIndex++;

                                          return (
                                            (isEditMode || isNewDeal) && isActive ? (
                                              <DealCapitalizationRowForm
                                                isActive={isActive}
                                                isSaving={isSaving}
                                                isSaveDisabled={isSaveDisabled}
                                                isInlineSaving={isInlineSaving}
                                                isSimpleView={isSimpleView}
                                                key={`${groupId}-${itemId}`}
                                                dealCapitalizationItemIndex={(
                                                  commonDealCapitalizationItemIndex + (rowIndexFrom || 0)
                                                )}
                                                dealCapitalizationItemData={dealCapitalizationItem}
                                                dealCapitalizationGroupData={dealCapitalizationGroupItem}
                                                dealCapitalizationCategory={dealCapitalizationCategory}
                                                dealCapitalizationItemDynamicData={(
                                                  dealCapitalizationItems
                                                  && dealCapitalizationItems[groupId]
                                                  && dealCapitalizationItems[groupId][itemId]
                                                )}
                                                dealCapitalizationNewItems={dealCapitalizationNewItems}
                                                dealCapitalizationItems={dealCapitalizationItems}
                                                currentUser={currentUser}
                                                inputRefs={inputRefs}
                                                deleteItemPermissions={deleteItemPermissions}
                                                dealCapitalizationTypeList={dealCapitalizationTypeList}
                                                dealCapitalizationGroupList={dealCapitalizationGroupList}
                                                onInlineSave={onInlineSave}
                                                onInlineCancel={onInlineCancel}
                                                onDeleteClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                  deleteClick(event, itemId, groupId);
                                                }}
                                                onItemChange={onItemChange}
                                              />
                                            ) : (
                                              <TableRow
                                                key={`${groupId}-${itemId}`}
                                                onClick={(): void => {
                                                  onRowClick(dealCapitalizationItem, dealCapitalizationGroupItem);
                                                }}
                                              >
                                                {
                                                  extendedTableFields && !!extendedTableFields.length
                                                  && extendedTableFields.map((
                                                    tableField: TableField,
                                                  ): ReactElement | boolean => (
                                                    !!tableField.component && (
                                                      <FilteredCell
                                                        key={tableField.id}
                                                        tableName={TABLE_NAME_DEAL_CAPITALIZATION}
                                                        columnName={tableField.name}
                                                        currentUser={currentUser}
                                                      >
                                                        {
                                                          tableField.component(
                                                            dealCapitalizationItem,
                                                            dealCapitalizationGroupItem,
                                                          )
                                                        }
                                                      </FilteredCell>
                                                    )
                                                  ))
                                                }
                                              </TableRow>
                                            )
                                          );
                                        })
                                      }
                                      {
                                        (isEditMode || isNewDeal)
                                        && dealCapitalizationNewItems[groupId]
                                        && !!dealCapitalizationNewItems[groupId].length
                                        && dealCapitalizationNewItems[groupId]
                                          .map((dealCapitalizationItem: DealCapitalizationItem): ReactElement => {
                                            const itemId: number = dealCapitalizationItem.id;
                                            const isInlineSaving: boolean = dealCapitalizationItemInlineSaveId !== null
                                            && dealCapitalizationGroupInlineSaveId !== null
                                              ? itemId === dealCapitalizationItemInlineSaveId
                                              && groupId === dealCapitalizationGroupInlineSaveId
                                              : isSaving;
                                            const commonDealCapitalizationItemIndex: number = commonRowIndex;
                                            commonRowIndex++;

                                            return (
                                              <DealCapitalizationRowForm
                                                isNewEntity
                                                isActive={!isNewDeal}
                                                isSaving={isSaving}
                                                isSaveDisabled={isSaveDisabled}
                                                isInlineSaving={isInlineSaving}
                                                isSimpleView={isSimpleView}
                                                key={`${groupId}-${itemId}`}
                                                dealCapitalizationItemIndex={(
                                                  commonDealCapitalizationItemIndex + (rowIndexFrom || 0)
                                                )}
                                                dealCapitalizationItemData={dealCapitalizationItem}
                                                dealCapitalizationGroupData={dealCapitalizationGroupItem}
                                                dealCapitalizationCategory={dealCapitalizationCategory}
                                                dealCapitalizationItemDynamicData={(
                                                  dealCapitalizationItems
                                                  && dealCapitalizationItems[groupId]
                                                  && dealCapitalizationItems[groupId][itemId]
                                                )}
                                                dealCapitalizationNewItems={dealCapitalizationNewItems}
                                                dealCapitalizationItems={dealCapitalizationItems}
                                                currentUser={currentUser}
                                                inputRefs={inputRefs}
                                                deleteItemPermissions={deleteItemPermissions}
                                                dealCapitalizationTypeList={dealCapitalizationTypeList}
                                                dealCapitalizationGroupList={dealCapitalizationGroupList}
                                                onInlineSave={onInlineSave}
                                                onInlineCancel={onInlineCancel}
                                                onDeleteClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                  deleteClick(event, itemId, groupId, true);
                                                }}
                                                onItemChange={onItemChange}
                                              />
                                            );
                                          })
                                      }
                                      {
                                        (isEditMode || isNewDeal) && (
                                          <CheckAccess permissions={createItemPermissions}>
                                            <TableRowAddNewItem
                                              label={`Add New ${groupTitle}`}
                                              colSpan={columnsTotal}
                                              onClick={(): void => {
                                                addNewItem(groupId);
                                              }}
                                            />
                                          </CheckAccess>
                                        )
                                      }
                                      {
                                        ((!isEditMode && !isNewDeal && dealCapitalizationGroupItem.summary)
                                          || ((isEditMode || isNewDeal) && dealCapitalizationGroupTotalItems
                                            && dealCapitalizationGroupTotalItems[groupId])
                                        ) && dealCapitalizationGroupItem.name
                                        && dealCapitalizationGroupItemList.length > 1 && (
                                          <TableRowSummary withNoHighlight withOffset={isEditMode || isNewDeal}>
                                            {
                                              extendedTableFields && !!extendedTableFields.length
                                              && extendedTableFields.map((tableField: TableField): ReactElement => (
                                                <VisibleCell
                                                  isFullTable={!isSimpleView}
                                                  isEditMode={isEditMode || isNewDeal}
                                                  tableField={tableField}
                                                  key={tableField.id}
                                                >
                                                  {
                                                    !!tableField.summaryComponent && (
                                                      <FilteredCell
                                                        tableName={TABLE_NAME_DEAL_CAPITALIZATION}
                                                        columnName={tableField.name}
                                                        currentUser={currentUser}
                                                      >
                                                        {
                                                          tableField.summaryComponent(
                                                            isEditMode || isNewDeal ? (
                                                              dealCapitalizationGroupTotalItems
                                                              && dealCapitalizationGroupTotalItems[groupId]
                                                            ) : dealCapitalizationGroupItem.summary,
                                                            `Total ${dealCapitalizationGroupItem.name}`,
                                                            isEditMode || isNewDeal,
                                                          )
                                                        }
                                                      </FilteredCell>
                                                    )
                                                  }
                                                </VisibleCell>
                                              ))
                                            }
                                          </TableRowSummary>
                                        )
                                      }
                                    </Fragment>
                                  );
                                })
                              }
                              {
                                ((!isEditMode && !isNewDeal && dealCapitalizationSummaryData)
                                  || ((isEditMode || isNewDeal) && dealCapitalizationTotalItems)) && (
                                  <TableRowSummary withNoHighlight withOffset={isEditMode || isNewDeal}>
                                    {
                                      extendedTableFields && !!extendedTableFields.length
                                      && extendedTableFields.map((tableField: TableField): ReactElement => (
                                        <VisibleCell
                                          isFullTable={!isSimpleView}
                                          isEditMode={isEditMode || isNewDeal}
                                          key={tableField.id}
                                          tableField={tableField}
                                        >
                                          {
                                            !!tableField.summaryComponent && (
                                              <FilteredCell
                                                tableName={TABLE_NAME_DEAL_CAPITALIZATION}
                                                columnName={tableField.name}
                                                currentUser={currentUser}
                                              >
                                                {
                                                  tableField.summaryComponent(
                                                    isEditMode || isNewDeal
                                                      ? dealCapitalizationTotalItems : dealCapitalizationSummaryData,
                                                    totalLabel,
                                                    isEditMode || isNewDeal,
                                                  )
                                                }
                                              </FilteredCell>
                                            )
                                          }
                                        </VisibleCell>
                                      ))
                                    }
                                  </TableRowSummary>
                                )
                              }
                            </Fragment>
                          )
                        }
                      </TableBody>
                    </Table>
                  </ContentListContainer>
                </Card>
              )
            }
            {
              ((isSimpleView && isMediumScreen) || (!isSimpleView && isSmallScreen)) && (
                <Fragment>
                  {
                    isSimpleView && (
                      <List>
                        {
                          dealCapitalizationGroupItemList && !!dealCapitalizationGroupItemList.length
                          && dealCapitalizationGroupItemList.map((
                            dealCapitalizationGroupItem: DealCapitalizationGroup,
                          ): ReactElement[] | boolean => {
                            const groupId: number = dealCapitalizationGroupItem.id;

                            return (
                              !!dealCapitalizationGroupItem.items && !!dealCapitalizationGroupItem.items.length
                              && dealCapitalizationGroupItem.items.map((
                                dealCapitalizationItem: DealCapitalizationItem,
                              ): ReactElement => {
                                const itemId: number = dealCapitalizationItem.id;

                                return (
                                  <ListItem
                                    isButton
                                    isExtended
                                    key={`${groupId}-${itemId}`}
                                    onClick={(): void => {
                                      onRowClick(dealCapitalizationItem, dealCapitalizationGroupItem, true);
                                    }}
                                  >
                                    <ListItemText
                                      isBlock
                                      subText={!isNewDeal ? dealCapitalizationGroupItem.name : ''}
                                    >
                                      {isNewDeal ? `New ${groupTitle}` : dealCapitalizationItem.name}
                                    </ListItemText>
                                    <ListItemText isBlock isBlockRight>
                                      {
                                        (isEditMode || isNewDeal) && (
                                          <CheckAccess permissions={deleteItemPermissions}>
                                            <Tooltip title="Delete">
                                              <ActionButton
                                                withSmallPadding
                                                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                  deleteClick(
                                                    event, itemId, groupId,
                                                  );
                                                }}
                                              >
                                                <RemoveIcon />
                                              </ActionButton>
                                            </Tooltip>
                                          </CheckAccess>
                                        )
                                      }
                                      <Tooltip title="Open in modal">
                                        <ActionButton
                                          withSmallPadding
                                          onClick={(): void => {
                                            onRowClick(dealCapitalizationItem, dealCapitalizationGroupItem, true);
                                          }}
                                        >
                                          <ArrowLinkIcon />
                                        </ActionButton>
                                      </Tooltip>
                                    </ListItemText>
                                  </ListItem>
                                );
                              })
                            );
                          })
                        }
                      </List>
                    )
                  }
                  {
                    !isSimpleView && (
                      <List>
                        {
                          dealCapitalizationGroupItemList && !!dealCapitalizationGroupItemList.length
                          && dealCapitalizationGroupItemList.map((
                            dealCapitalizationGroupItem: DealCapitalizationGroup,
                          ): ReactElement => {
                            const groupId: number = dealCapitalizationGroupItem.id;

                            return (
                              <div key={groupId}>
                                <ListGroupHeader>
                                  {dealCapitalizationGroupItem.name}
                                </ListGroupHeader>
                                {
                                  dealCapitalizationGroupItem.items && !!dealCapitalizationGroupItem.items.length
                                  && dealCapitalizationGroupItem.items.map((
                                    dealCapitalizationItem: DealCapitalizationItem,
                                  ): ReactElement => {
                                    const itemId: number = dealCapitalizationItem.id;

                                    return (
                                      <ListItem
                                        isButton
                                        isExtended
                                        key={`${groupId}-${itemId}`}
                                        onClick={(): void => {
                                          onRowClick(dealCapitalizationItem, dealCapitalizationGroupItem, true);
                                        }}
                                      >
                                        <ListItemText
                                          isBlock
                                          subText={!isNewDeal ? dealCapitalizationGroupItem.name : ''}
                                        >
                                          {isNewDeal ? `New ${groupTitle}` : dealCapitalizationItem.name}
                                        </ListItemText>
                                        <ListItemText isBlock isBlockRight>
                                          {
                                            !isEditMode && !isNewDeal ? dealCapitalizationItem.amount : (
                                              <Fragment>
                                                <CheckAccess permissions={deleteItemPermissions}>
                                                  <Tooltip title="Delete">
                                                    <ActionButton
                                                      withSmallPadding
                                                      onClick={(
                                                        event: React.MouseEvent<Element, MouseEvent>,
                                                      ): void => {
                                                        deleteClick(event, itemId, groupId);
                                                      }}
                                                    >
                                                      <RemoveIcon />
                                                    </ActionButton>
                                                  </Tooltip>
                                                </CheckAccess>
                                                <Tooltip title="Open in modal">
                                                  <ActionButton
                                                    withSmallPadding
                                                    onClick={(): void => {
                                                      onRowClick(
                                                        dealCapitalizationItem, dealCapitalizationGroupItem, true,
                                                      );
                                                    }}
                                                  >
                                                    <ArrowLinkIcon />
                                                  </ActionButton>
                                                </Tooltip>
                                              </Fragment>
                                            )
                                          }
                                        </ListItemText>
                                      </ListItem>
                                    );
                                  })
                                }
                                {
                                  (isEditMode || isNewDeal) && dealCapitalizationNewItems[groupId]
                                  && !!dealCapitalizationNewItems[groupId].length
                                  && dealCapitalizationNewItems[groupId]
                                    .map((dealCapitalizationItem: DealCapitalizationItem): ReactElement => {
                                      const itemId: number = dealCapitalizationItem.id;

                                      return (
                                        <ListItem
                                          isButton
                                          isExtended
                                          isActive={!isNewDeal}
                                          key={`${groupId}-${itemId}`}
                                          onClick={(): void => {
                                            onRowClick(dealCapitalizationItem, dealCapitalizationGroupItem, true);
                                          }}
                                        >
                                          <ListItemText isBlock>
                                            {`New ${groupTitle}`}
                                          </ListItemText>
                                          <ListItemText isBlock isBlockRight>
                                            <Tooltip title="Delete">
                                              <ActionButton
                                                withSmallPadding
                                                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                  deleteClick(event, itemId, groupId, true);
                                                }}
                                              >
                                                <RemoveIcon />
                                              </ActionButton>
                                            </Tooltip>
                                            <Tooltip title="Open in modal">
                                              <ActionButton
                                                withSmallPadding
                                                onClick={(): void => {
                                                  onRowClick(dealCapitalizationItem, dealCapitalizationGroupItem, true);
                                                }}
                                              >
                                                <ArrowLinkIcon />
                                              </ActionButton>
                                            </Tooltip>
                                          </ListItemText>
                                        </ListItem>
                                      );
                                    })
                                }
                                {
                                  (isEditMode || isNewDeal) && (
                                    <CheckAccess permissions={createItemPermissions}>
                                      <Card
                                        isContainer
                                        isSelectable
                                        withSmallPadding
                                        onClick={(): void => {
                                          addNewItem(groupId);
                                        }}
                                      >
                                        <ActionButtonNewItem withNoHighlight label={`Add New ${groupTitle}`} />
                                      </Card>
                                    </CheckAccess>
                                  )
                                }
                              </div>
                            );
                          })
                        }
                      </List>
                    )
                  }
                  {
                    !isEditMode && !isNewDeal && !isSimpleView && (
                      <ListItemSummary>
                        <ListItemText isBlock>
                          {totalLabel}
                        </ListItemText>
                        <ListItemText isBlock isBlockRight>
                          {dealCapitalizationSummaryData && dealCapitalizationSummaryData.amount}
                        </ListItemText>
                      </ListItemSummary>
                    )
                  }
                </Fragment>
              )
            }
          </ExpansionGroup>
        )
      }
    </CheckAccess>
  );
});

export default DealCapitalization;
